import { useState, useEffect } from "react";
import { renderToString } from "react-dom/server";

import styles from "./FaqPage.module.scss";

import { FAQ_CONTENT } from "~/utils/Constants";
import { generateFAQPageSchema, generateFAQHubSchema } from "~/utils/Schema/faq";

import Head from "~/components/Meta/Head";
import FaqHeader from "~/components/Faq/FaqHeader";
import FaqSearchResult from "~/components/Faq/FaqSearchResult";
import FaqPillButtonContainer from "~/components/Faq/FaqPillButtonContainer";

import Link from "next/link";

const FaqPage = ({ selected, description }) => {
  const pillButtons = [
    { value: "for-buyers", label: "For Buyers" },
    { value: "for-sellers", label: "For Sellers" },
    { value: "for-agents", label: "For Agents" },
    { value: "working-with-an-agent", label: "Working With An Agent" },
    { value: "how-we-work", label: "How We Work" },
  ];

  const pillButtonLookup = (slug) => {
    return pillButtons.find((pillButton) => pillButton.value === slug);
  };

  const [selectedPill, setSelectedPill] = useState(
    pillButtonLookup(selected) || null
  );
  const [search, setSearch] = useState("");
  const [staleSearchValue, setStaleSearchValue] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentFaqContent, setCurrentFaqContent] = useState(() => {
    if (selected != null) {
      return FAQ_CONTENT.filter(
        (content) => content.slug === selectedPill.value
      );
    }
    return [];
  });
  const [openedFaqs, setOpenedFaqs] = useState([]);
  const [hideUnselectedState, setHideUnselectedState] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (selectedPill) {
      const filteredFaqContent = FAQ_CONTENT.filter(
        (content) => content.slug === selectedPill.value
      );
      setCurrentFaqContent(filteredFaqContent);
    }
  }, [selectedPill]);

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = () => {
    if (!search && !selectedPill) {
      setCurrentFaqContent([]);
      setSearchSubmitted(false);
    }

    if (!search && selectedPill?.label) {
      setSearchSubmitted(false);
      const filteredFaqContent = FAQ_CONTENT.filter(
        (content) => content.slug === selectedPill.value
      );
      setCurrentFaqContent(filteredFaqContent);
    } else {
      if (search) {
        setOpenedFaqs([]);
        setSearchSubmitted(true);

        const filteredSearchFaqContent = FAQ_CONTENT.filter((content) => {
          const { answers, question } = content;
          const foundAnswerMatch = answers.find((answer) =>
            renderToString(answer).toLowerCase().includes(search.toLowerCase())
          );
          return (
            foundAnswerMatch ||
            question.toLowerCase().includes(search.toLowerCase())
          );
        });

        setCurrentFaqContent(
          filteredSearchFaqContent.map((content, index) => {
            if (index === 0) {
              content.sectionHeader = pillButtonLookup(content.slug).label;
              return content;
            } else if (
              filteredSearchFaqContent[index - 1].slug === content.slug
            ) {
              content.sectionHeader = null;
              return content;
            } else {
              content.sectionHeader = pillButtonLookup(content.slug).label;
              return content;
            }
          })
        );
        setStaleSearchValue(search);
      }
    }
  };

  const title = () => {
    if (selectedPill?.label) {
      return selectedPill.label.charAt(0).toLowerCase() === "f"
        ? `FAQs ${
            selectedPill.label.charAt(0).toLowerCase() +
            selectedPill.label.slice(1)
          }`
        : `FAQs on ${selectedPill.label}`;
    } else {
      return "FAQs";
    }
  };

  const hideContentContainer =
    searchSubmitted && currentFaqContent.length === 0;

  // Generate appropriate schema based on whether this is hub or category page
  const schema = (() => {
    if (selected && currentFaqContent.length >= 1) {
      // Category page with FAQs - use FAQPage schema
      return generateFAQPageSchema(
        currentFaqContent,
        selectedPill.value,
        selectedPill.label
      );
    } else if (!selected) {
      // Hub page - use CollectionPage schema
      return generateFAQHubSchema(description);
    }
    return null;
  })();

  return (
    <>
      <Head
        description={description}
        title={`Frequently Asked Questions ${
          selectedPill?.label ? selectedPill.label : "(FAQ"
        } | RealEstateAgents.com`}
        schema={schema}
      />
      <div className={styles["Faq"]}>
        <FaqHeader
          handleSearch={handleSearch}
          handleEnterPress={handleEnterPress}
          handleSearchSubmit={handleSearchSubmit}
          search={search}
          selectedPill={selectedPill}
        />
        <FaqPillButtonContainer
          searchSubmitted={searchSubmitted}
          setSelectedPill={setSelectedPill}
          setSearch={setSearch}
          selectedPill={selectedPill}
          currentFaqContent={currentFaqContent}
          staleSearchValue={staleSearchValue}
          pillButtons={pillButtons}
          setHideUnselectedState={setHideUnselectedState}
        />
        {!selectedPill?.label && !hideUnselectedState && !searchSubmitted && (
          <div className={styles["Faq__unselected-state"]}>
            <h3 className={styles["Faq__sub-heading"]}>
              Select a filter or search above to view our FAQ's
            </h3>
          </div>
        )}
        {searchSubmitted && currentFaqContent.length === 0 && (
          <div className={styles["Faq__search-header-wrapper"]}>
            {searchSubmitted && (
              <h2 className={styles["Faq__sub-heading"]}>
                {currentFaqContent?.length}{" "}
                {currentFaqContent?.length === 1 ? "result" : "results"} for
                &quot;
                {staleSearchValue}
                &quot;
              </h2>
            )}

            <p className={styles["Faq__sub-heading-zero"]}>
              You can try a different search phrase or{" "}
              <Link href={"/faq"} className={styles["Faq__sub-heading-link"]}>
                return to the FAQ page.
              </Link>
            </p>
          </div>
        )}
        {!hideContentContainer && (
          <div
            className={`${styles["Faq__main-content-wrapper"]} ${
              !searchSubmitted && styles["--padding"]
            }`}
          >
            <div className={styles["Faq__main-content"]}>
              {selectedPill?.label && !searchSubmitted && (
                <h3
                  className={`${styles["Faq__sub-heading"]}  ${styles["--left"]}
                  `}
                >
                  {title()}
                </h3>
              )}
              <div className={styles["Faq__search-results-container"]}>
                {currentFaqContent?.map((content, index) => {
                  return (
                    <div key={index}>
                      {content.sectionHeader && searchSubmitted ? (
                        <div className={styles["Faq__section-header-wrapper"]}>
                          <h3
                            className={`${styles["Faq__sub-heading"]}  ${styles["--left"]}
                  `}
                          >
                            FAQ {content.sectionHeader}
                          </h3>

                          <FaqSearchResult
                            content={content}
                            isInitialized={isInitialized}
                            openedFaqs={openedFaqs}
                            setOpenedFaqs={setOpenedFaqs}
                          />
                        </div>
                      ) : (
                        <FaqSearchResult
                          content={content}
                          isInitialized={isInitialized}
                          openedFaqs={openedFaqs}
                          setOpenedFaqs={setOpenedFaqs}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FaqPage;
